import { useEffect, useState } from 'react';

import { SPLIT_MEDIA_QUERY } from '../design/tokens';

/**
 * True when the two-column split layout is active.
 *
 * Uses `SPLIT_MEDIA_QUERY`, built from the same pixel constant as
 * `--breakpoint-split` in globals.css. That shared constant is the point: if the CSS
 * switched columns at a different width than this hook reports, the layout and the
 * IntersectionObserver root would disagree, and between those widths every revealed
 * element would stay hidden. See the comment on SPLIT_BREAKPOINT_PX.
 */
export function useSplitLayout(): boolean {
  const [isSplit, setIsSplit] = useState(() => matchSplit());

  useEffect(() => {
    const query = window.matchMedia(SPLIT_MEDIA_QUERY);
    const onChange = () => {
      setIsSplit(query.matches);
    };

    // No initial setState here: useState is already seeded from the same query, and
    // calling it synchronously in the effect only triggers a cascading render.
    query.addEventListener('change', onChange);

    return () => {
      query.removeEventListener('change', onChange);
    };
  }, []);

  return isSplit;
}

function matchSplit(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(SPLIT_MEDIA_QUERY).matches;
}
